import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PrivateRoute } from './components/PrivateRoute';
import { Layout } from './components/Layout';
import { AdminLayout } from './components/AdminLayout';
import { UserType } from './types';

import { AdminLogin } from './pages/admin/AdminLogin';
import ForgotPassword from './pages/admin/ForgotPassword';
import ResetPassword from './pages/admin/ResetPassword';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { ApplicationsList } from './pages/admin/ApplicationsList';
import { ApplicationDetails } from './pages/admin/ApplicationDetails';
import { LoansList } from './pages/admin/LoansList';
import { PendingPayments } from './pages/admin/PendingPayments';
import { VerifyPayment } from './pages/admin/VerifyPayment';
import { Settings } from './pages/admin/Settings';
import { CustomersList } from './pages/admin/CustomersList';
import { Customer360 } from './pages/admin/Customer360';
import { Reports } from './pages/admin/Reports';
import { StaffList } from './pages/admin/StaffList';
import { StaffDetails } from './pages/admin/StaffDetails';
import EmailTemplates from './pages/admin/EmailTemplates';
import SupportTickets from './pages/admin/SupportTickets';
import SupportTicketDetails from './pages/admin/SupportTicketDetails';
import FAQs from './pages/admin/FAQs';
import { SchedulerDashboard } from './pages/admin/SchedulerDashboard';
import { NotificationAnalytics } from './pages/admin/NotificationAnalytics';
import { NotificationQueue } from './pages/admin/NotificationQueue';
import NotificationTemplates from './pages/admin/NotificationTemplates';

import './styles/designSystem.css';
import './App.css';

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AuthProvider>
        <Routes>
          {/* Redirect root to login */}
          <Route path="/" element={<Navigate to="/admin/login" replace />} />

          {/* Admin Login */}
          <Route path="/admin/login" element={<Layout><AdminLogin /></Layout>} />
          <Route path="/admin/forgot-password" element={<ForgotPassword />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />

          {/* Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><AdminDashboard /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/applications"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><ApplicationsList /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/applications/:id"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><ApplicationDetails /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/loans"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><LoansList /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/payments/pending"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><PendingPayments /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/payments/:id/verify"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><VerifyPayment /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><Settings /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/customers"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><CustomersList /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/customers/:userId"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><Customer360 /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><Reports /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/staff"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><StaffList /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/staff/:staffId"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><StaffDetails /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/email-templates"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><EmailTemplates /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/support"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><SupportTickets /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/support/tickets/:ticketId"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><SupportTicketDetails /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/faqs"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><FAQs /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/scheduler"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><SchedulerDashboard /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/notification-analytics"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><NotificationAnalytics /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/notification-queue"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><NotificationQueue /></AdminLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/notification-templates"
            element={
              <PrivateRoute requiredType={UserType.STAFF}>
                <AdminLayout><NotificationTemplates /></AdminLayout>
              </PrivateRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
